import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./AdminSchoolApprovals.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com";

type SchoolSubmission = {
  submission_id?: number;
  status?: string;
  org_code?: string;
  school_name?: string;
  uploaded_at?: string;
  filename?: string;
  total_students?: number;
  id?: number;
  org_id?: number;
  row_count?: number;
  created_at?: string;
  submitted_at?: string;
};

const AdminSchoolApprovals: React.FC = () => {
  const [status, setStatus] = useState<string>("PENDING");
  const [rows, setRows] = useState<SchoolSubmission[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);

  const token = useMemo(() => localStorage.getItem("admin_token") || "", []);
  const authHeaders = useMemo(() => {
    const h: Record<string, string> = { Accept: "application/json" };
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }, [token]);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const urls = [
        `${API_BASE}/school/admin/roster-submissions`,
        `${API_BASE}/admin/school-submissions?status=${encodeURIComponent(status)}`,
      ];

      let payload: any = null;
      let lastError = "Failed to load submissions";
      for (const url of urls) {
        const res = await fetch(url, { headers: authHeaders });
        if (res.ok) {
          payload = await res.json();
          break;
        }
        lastError = `HTTP ${res.status} ${res.statusText}`;
        if (res.status !== 404 && res.status !== 405) break;
      }
      if (payload == null) throw new Error(lastError);

      const sourceRows = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : [];
      const normalized = sourceRows
        .map((item: any) => ({
          ...item,
          submission_id: Number(item?.submission_id || item?.id || 0) || undefined,
          uploaded_at: item?.uploaded_at || item?.submitted_at || item?.created_at,
          total_students: item?.total_students ?? item?.row_count,
        }))
        .filter((item: SchoolSubmission) =>
          status === "ALL" || String(item.status || "").toUpperCase() === status
        );
      setRows(normalized);
    } catch (e: any) {
      setErr(e?.message || "Failed to load submissions");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status, authHeaders]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const approve = useCallback(
    async (submissionId: number) => {
      // Backend expects Form("notes"); URLSearchParams is clean + compatible.
      const body = new URLSearchParams({ notes: "" });
      let res = await fetch(`${API_BASE}/school/admin/roster-submissions/${submissionId}/approve`, {
        method: "POST",
        headers: authHeaders,
      });
      if (res.status === 404 || res.status === 405) {
        res = await fetch(`${API_BASE}/admin/school-submissions/${submissionId}/approve`, {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/x-www-form-urlencoded" },
          body,
        });
      }
      if (!res.ok) throw new Error(`Approve failed (${res.status})`);
      await fetchSubmissions();
    },
    [authHeaders, fetchSubmissions]
  );

  const reject = useCallback(
    async (submissionId: number) => {
      const body = new URLSearchParams({ notes: "" });
      let res = await fetch(`${API_BASE}/school/admin/roster-submissions/${submissionId}/reject`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({ reason: "" }),
      });
      if (res.status === 404 || res.status === 405) {
        res = await fetch(`${API_BASE}/admin/school-submissions/${submissionId}/reject`, {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/x-www-form-urlencoded" },
          body,
        });
      }
      if (!res.ok) throw new Error(`Reject failed (${res.status})`);
      await fetchSubmissions();
    },
    [authHeaders, fetchSubmissions]
  );

  return (
    <div className="school-approvals-page">
      <div className="school-approvals-panel">
        <div className="school-approvals-topbar">
          <div className="school-approvals-title">
            <h2>School Approvals</h2>
            <p>Review roster upload submissions and approve/reject.</p>
          </div>

          <div className="school-approvals-actions">
            <select
              className="school-approvals-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Filter status"
            >
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="ALL">All</option>
            </select>

            <button className="btn-soft" onClick={fetchSubmissions} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        {err && <div className="school-approvals-error">{err}</div>}

        <div className="school-approvals-body">
          {loading ? (
            <div className="school-approvals-empty">Loading submissions…</div>
          ) : rows.length === 0 ? (
            <div className="school-approvals-empty">
              <div className="empty-title">No submissions found</div>
              <div className="empty-sub">
                When schools upload rosters, they’ll show up here for approval.
              </div>
            </div>
          ) : (
            <table className="school-approvals-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>School</th>
                  <th>Org Code</th>
                  <th>Status</th>
                  <th>Uploaded</th>
                  <th>File</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const id = r.submission_id ?? -1;
                  return (
                    <tr key={`${id}-${idx}`}>
                      <td>{r.submission_id ?? "—"}</td>
                      <td>{r.school_name ?? "—"}</td>
                      <td>{r.org_code ?? "—"}</td>
                      <td>{r.status ?? "—"}</td>
                      <td>{r.uploaded_at ?? "—"}</td>
                      <td className="mono">{r.filename ?? "—"}</td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          className="btn-approve"
                          onClick={() => id > 0 && approve(id)}
                          disabled={id <= 0}
                          title={id > 0 ? "Approve" : "No submission_id provided"}
                        >
                          Approve
                        </button>
                        <button
                          className="btn-reject"
                          onClick={() => id > 0 && reject(id)}
                          disabled={id <= 0}
                          title={id > 0 ? "Reject" : "No submission_id provided"}
                        >
                          Reject
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSchoolApprovals;
